import styled from '@emotion/styled';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import { prop } from 'ramda';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import InvalidMessage from 'App/common/styles/InvalidMessage';
import { NumberField } from 'Shared/form/NumberField';
import SelectField from 'Shared/form/SelectField';
import ToggleSwitchField from 'Shared/form/ToggleSwitchField';
import withFormikFields from 'Shared/form/withFormikFields';
import { numericRiskTypeOptions } from '../constants';
import useFetchAssessmentRiskOptions from '../hooks/useFetchAssessmentRiskOptions';
import questionPropType from '../types/questionPropType';

const ScoringToggleRowStyle = styled(Row)`
    display: flex;

    .col-md-6 {
        z-index: 1;
    }
`;

const ScoringDetailRowStyle = styled(Row)`
    margin-top: -2.5em;

    .col-md-4:first-of-type .control-label {
        visibility: hidden;
    }
`;

const ScoreRangeRowStyle = styled(Row)`
    margin-bottom: 0.5em;
`;

const QuestionScoringField = ({ question, fieldValues: { scoringEnabled } }) => {
    const { assessmentId } = useParams();
    const { data: assessmentRiskOptions = [], isLoading: isRiskOptionsLoading } =
        useFetchAssessmentRiskOptions(assessmentId);
    const [{ value: numericRiskType }] = useField(`questions.${question.question_id}.attributes.numericRiskType`);
    const [{ value: numericRiskRanges = [{}] }, { error }, { setValue: setNumericRiskRanges }] = useField({
        name: `questions.${question.question_id}.attributes.numericRiskRanges`,
        validate: (value = []) => {
            for (let i = 0; i < value.length - 1; i += 1) {
                const currRange = value[i];
                const rangesToCheck = value.slice(i + 1);
                if (
                    rangesToCheck.find(
                        (checkRange) =>
                            (currRange.start >= checkRange.start && currRange.start <= checkRange.end) ||
                            (currRange.end >= checkRange.start && currRange.end <= checkRange.end)
                    )
                ) {
                    return 'Numeric scoring ranges cannot overlap.';
                }
            }
        },
    });

    if (!scoringEnabled) {
        return null;
    }

    if (!['select', 'radio', 'multiselect', 'checkbox', 'number'].includes(question.type)) {
        return null;
    }

    return (
        <>
            <ScoringToggleRowStyle>
                <Col md={6}>
                    <ToggleSwitchField
                        fieldName={`questions.${question.question_id}.attributes.scoring_enabled`}
                        label="Scoring"
                    />
                </Col>
            </ScoringToggleRowStyle>

            {question.attributes.scoring_enabled && (
                <>
                    <ScoringDetailRowStyle>
                        <Col md={4}>
                            <SelectField
                                fieldName={`questions.${question.question_id}.attributes.risk_config`}
                                label="Scoring Config"
                                options={assessmentRiskOptions}
                                isLoading={isRiskOptionsLoading}
                                getOptionLabel={(o) =>
                                    assessmentRiskOptions.find((risk) => risk.id === o.id)?.risk_name
                                }
                                getOptionValue={prop('id')}
                                required
                            />
                        </Col>
                        <Col md={4}>
                            <NumberField
                                fieldName={`questions.${question.question_id}.attributes.risk_weight`}
                                label="Weight"
                                placeholder="Weight"
                                required
                                debounce
                            />
                        </Col>
                        {question.type === 'number' && (
                            <Col md={4}>
                                <SelectField
                                    fieldName={`questions.${question.question_id}.attributes.numericRiskType`}
                                    label="Select Weight Option"
                                    options={numericRiskTypeOptions}
                                    required
                                />
                            </Col>
                        )}
                    </ScoringDetailRowStyle>
                    {numericRiskType === numericRiskTypeOptions[1] && (
                        <>
                            <Row>
                                <Col md={3}>
                                    <legend className="field-required">Start</legend>
                                </Col>
                                <Col md={3}>
                                    <legend className="field-required">End</legend>
                                </Col>
                                <Col md={3}>
                                    <legend className="field-required">Scoring Multiplier</legend>
                                </Col>
                                <Col md={3}>
                                    <Button
                                        variant="link"
                                        onClick={() => {
                                            setNumericRiskRanges([...numericRiskRanges, {}]);
                                        }}
                                    >
                                        Add Range
                                    </Button>
                                </Col>
                            </Row>
                            {numericRiskRanges.map((range, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <ScoreRangeRowStyle key={index}>
                                    <Col md={3}>
                                        <NumberField
                                            fieldName={`questions.${question.question_id}.attributes.numericRiskRanges[${index}].start`}
                                            placeholder="Start"
                                            aria-label="Start"
                                            required
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <NumberField
                                            fieldName={`questions.${question.question_id}.attributes.numericRiskRanges[${index}].end`}
                                            placeholder="End"
                                            aria-label="End"
                                            required
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <NumberField
                                            fieldName={`questions.${question.question_id}.attributes.numericRiskRanges[${index}].value`}
                                            placeholder="Scoring Multiplier"
                                            aria-label="Scoring Multiplier"
                                            required
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Button
                                            variant="link"
                                            disabled={numericRiskRanges.length <= 1}
                                            onClick={() => {
                                                const updatedRanges = [...numericRiskRanges];
                                                updatedRanges.splice(index, 1);
                                                setNumericRiskRanges(updatedRanges);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Col>
                                </ScoreRangeRowStyle>
                            ))}
                            {typeof error === 'string' && <InvalidMessage>{error}</InvalidMessage>}
                        </>
                    )}
                </>
            )}
        </>
    );
};

QuestionScoringField.propTypes = {
    question: questionPropType.isRequired,
    fieldValues: PropTypes.shape({
        scoringEnabled: PropTypes.bool,
    }).isRequired,
};

export default withFormikFields({ WrappedComponent: QuestionScoringField, fieldNames: ['scoringEnabled'] });
